import { Button, Card, cn } from "@heroui/react";
import { ArrowRepeat, ChatDots, FilePdfFill } from "react-bootstrap-icons";

import { generateRecommendationsPDF } from "@/app/helpers/pdf-generator";
import { recordRecommendationClick } from "@/app/helpers/user-actions";
import { Recommendation } from "@/types/dataSchema";

interface RecommendationActionsProps {
  recommendations: Recommendation[];
  answers: Record<string, any>;
  journeyData: any;
  hasContactRecommendations: boolean;
  onGeneralContactClick: () => void;
}

export function RecommendationActions({
  recommendations,
  answers,
  journeyData,
  hasContactRecommendations,
  onGeneralContactClick,
}: RecommendationActionsProps) {
  const handlePDFDownload = async () => {
    // Record the PDF download action
    recordRecommendationClick({
      recommendationId: "pdf_report",
      recommendationName: "PDF Report",
      actionId: "download_pdf",
      actionName: "Download PDF Report",
      actionUrl: "",
      isCta: true,
    });

    // Generate and download PDF
    await generateRecommendationsPDF({
      recommendations,
      answers,
      journeyData,
    });
  };

  const handleRestart = () => {
    // Record the restart action
    recordRecommendationClick({
      recommendationId: "restart_onboarding",
      recommendationName: "Restart Onboarding",
      actionId: "restart_button",
      actionName: "Restart",
      actionUrl: "",
      isCta: false,
    });

    window.location.reload();
  };

  return (
    <div className="mt-24 mb-16">
      <div className="max-w-4xl mx-auto">
        {/* PDF Download Section */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-full mb-6">
            <FilePdfFill className="w-8 h-8 text-white" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-4">
            Take Your Recommendations With You
          </h3>
          <p className="text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
            Download a comprehensive PDF report with all your answers and our
            personalized recommendations.
          </p>
          <Button
            className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-semibold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 hover:shadow-xl hover:shadow-indigo-500/20 text-lg"
            size="lg"
            onPress={handlePDFDownload}
          >
            <span className="flex items-center gap-3">
              Download this customized report as PDF
              <FilePdfFill className="w-5 h-5" />
            </span>
          </Button>
        </div>

        {/* Quick Actions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          {/* Contact Section */}
          {!hasContactRecommendations && (
            <Card className="p-8 bg-gradient-to-br from-slate-800/80 to-slate-900/60 border border-slate-600/30 backdrop-blur-sm hover:border-slate-500/50 transition-all duration-300">
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full mb-4">
                  <ChatDots className="w-6 h-6 text-white" />
                </div>
                <h4 className="text-lg font-semibold text-white mb-2">
                  Need More Help?
                </h4>
                <p className="text-gray-400 text-sm mb-6">
                  Our support team is ready to assist you with any questions or
                  custom requirements.
                </p>
                <Button
                  className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200"
                  variant="solid"
                  onPress={onGeneralContactClick}
                >
                  Contact Our Support Team
                </Button>
              </div>
            </Card>
          )}

          {/* Restart Section */}
          <Card
            className={cn(
              "p-8 bg-gradient-to-br from-slate-800/80 to-slate-900/60 border border-slate-600/30 backdrop-blur-sm hover:border-slate-500/50 transition-all duration-300",
              !hasContactRecommendations
                ? ""
                : "md:col-span-2 max-w-md mx-auto",
            )}
          >
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-full mb-4">
                <ArrowRepeat className="w-6 h-6 text-white" />
              </div>
              <h4 className="text-lg font-semibold text-white mb-2">
                Start Over?
              </h4>
              <p className="text-gray-400 text-sm mb-6">
                Want to explore different options or try a different approach?
                Restart the onboarding process.
              </p>
              <Button
                className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-500 hover:to-orange-500 text-white font-medium py-3 px-6 rounded-lg transition-all duration-200"
                variant="solid"
                onPress={handleRestart}
              >
                Restart Onboarding
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
